import { storePath } from '../address.js';
import {
  type DesignLink,
  type DesignPage,
  type Localized,
  type StoreDesign,
  legacyDesign,
  storeDesignSchema,
} from '@arabat/contracts';
import { cn } from '@arabat/ui';
import { readableOn, useApi } from '@arabat/web-core';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { StoreSkeleton } from './skeleton.js';

/**
 * Store design on the storefront (ADR-0128). Shoppers get the published design; inside the
 * console's preview frame (named `arabat-design-preview`) the merchant's draft is shown and
 * replaced live by the editor through `postMessage` (same origin only).
 */
const PREVIEW_FRAME_NAME = 'arabat-design-preview';
export const isPreview = () => typeof window !== 'undefined' && window.name === PREVIEW_FRAME_NAME;
/**
 * A full-page preview (ADR-0146): the console opens `/store/{slug}?preview=1` in a new tab so
 * the merchant sees the draft store as a page. The API answers only to the merchant's own
 * management session, so anyone else gets nothing more than the public store would show.
 */
export function enterFullPagePreview(search: string) {
  if (typeof window === 'undefined' || window.top !== window.self) return;
  // Only the address with `?preview=1` is a preview: without it (a shopper who removed it, a
  // later visit in the same tab) the page is the public store again (security review).
  if (new URLSearchParams(search).get('preview') === '1') window.name = PREVIEW_FRAME_NAME;
  else if (window.name === PREVIEW_FRAME_NAME) window.name = '';
}

/**
 * A top-level preview whose API refuses the visitor (no management session: a shared
 * `?preview=1` link) goes back to the public store at once.
 */
export function leaveRefusedPreview(status: number | undefined) {
  if (typeof window === 'undefined' || window.top !== window.self) return;
  if ((status === 401 || status === 403) && window.name === PREVIEW_FRAME_NAME) {
    window.name = '';
    const url = new URL(window.location.href);
    url.searchParams.delete('preview');
    window.location.replace(url.toString());
  }
}

type PreviewMessage =
  | { type: 'arabat:design'; design: unknown }
  | { type: 'arabat:navigate'; page: DesignPage | 'checkout'; productId?: string | null }
  | { type: 'arabat:focus'; sectionId: string }
  | { type: 'arabat:scroll'; to: 'top' | 'footer' };

const DesignContext = createContext<StoreDesign | null>(null);

/**
 * The store's design (the draft inside the preview). The store layout starts it together with
 * the store itself, so the page does not wait for one after the other.
 */
export function useDesignQuery(slug: string) {
  const api = useApi();
  const preview = isPreview();
  return useQuery({
    queryKey: ['store', slug, 'design', preview],
    queryFn: async () =>
      preview ? (await api.online.design()).draft : (await api.store.design(slug)).design,
    retry: false,
    staleTime: 60_000,
  });
}

export function DesignProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const preview = isPreview();
  const navigate = useNavigate();
  const published = useDesignQuery(slug);
  const [live, setLive] = useState<StoreDesign | null>(null);

  useEffect(() => {
    if (!preview) return;
    const onMessage = (event: MessageEvent<PreviewMessage>) => {
      if (event.origin !== window.location.origin || event.source !== window.parent) return;
      const msg = event.data;
      if (msg?.type === 'arabat:design') {
        const parsed = storeDesignSchema.safeParse(msg.design);
        if (parsed.success) setLive(parsed.data);
      } else if (msg?.type === 'arabat:navigate') {
        // The product page opens the product the editor picked (or the list when none); the
        // page moves an id to the product's readable address.
        const path = {
          home: '',
          products: '/products',
          categories: '/categories',
          product: msg.productId ? `/products/${encodeURIComponent(msg.productId)}` : '/products',
          cart: '/cart',
          checkout: '/checkout',
        }[msg.page];
        void navigate(storePath(slug, path));
      } else if (msg?.type === 'arabat:scroll') {
        // The editor's header and footer tabs bring their part of the store into view.
        requestAnimationFrame(() => {
          if (msg.to === 'footer')
            document
              .querySelector('[data-testid="store-footer"]')
              ?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
          else window.scrollTo?.({ top: 0, behavior: 'smooth' });
        });
      } else if (msg?.type === 'arabat:focus') {
        document
          .getElementById(`section-${msg.sectionId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    window.addEventListener('message', onMessage);
    // Ask the editor for its current draft (it may hold unsaved changes).
    window.parent.postMessage({ type: 'arabat:design-ready' }, window.location.origin);
    return () => window.removeEventListener('message', onMessage);
  }, [preview, navigate, slug]);

  // While the design loads the page's outline shows, so the legacy layout never flashes.
  if (published.isPending && !live) return <StoreSkeleton />;
  const design = live ?? published.data ?? legacyDesign();
  return <DesignContext.Provider value={design}>{children}</DesignContext.Provider>;
}

export function useDesign(): StoreDesign {
  return useContext(DesignContext) ?? legacyDesign();
}

/** Picks the text in the shopper's language, falling back to the other one. */
export function useText() {
  const { i18n } = useTranslation();
  const ar = i18n.language.startsWith('ar');
  return (value: Localized | null | undefined, fallback = '') =>
    (ar ? value?.ar || value?.en : value?.en || value?.ar) || fallback;
}

export function assetUrl(slug: string, id: string | null): string | null {
  if (!id) return null;
  return isPreview()
    ? `/api/v1/manager/online/design/assets/${id}/file`
    : `/api/v1/store/${encodeURIComponent(slug)}/assets/${id}`;
}

/** Where a design link goes: a route inside the store, or an outside address. */
export function linkTarget(
  slug: string,
  link: DesignLink | null,
  /** Readable addresses of products and groups (`useStorePaths`, migration 0107). */
  paths: { product: (id: string) => string; group: (id: string) => string } = {
    product: (id) => `/products/${encodeURIComponent(id)}`,
    group: (id) => `/products?category=${encodeURIComponent(id)}`,
  },
): { to: string; external: boolean } | null {
  if (!link) return null;
  const to = (path = '') => storePath(slug, path);
  switch (link.kind) {
    case 'page':
      return {
        to: {
          home: to(),
          products: to('/products'),
          categories: to('/categories'),
          cart: to('/cart'),
          account: to('/account'),
          brands: to('/brands'),
          wishlist: to('/wishlist'),
        }[link.page],
        external: false,
      };
    case 'category':
      return { to: to(paths.group(link.categoryId)), external: false };
    case 'product':
      return { to: to(paths.product(link.productId)), external: false };
    case 'url':
      return link.url.startsWith('/')
        ? { to: to(link.url === '/' ? '' : link.url), external: false }
        : { to: link.url, external: true };
  }
}

// --------------------------------------------------------------------------- styling
/** Card surface by the design's card style. */
export function useCard() {
  const { style } = useDesign();
  return {
    bordered: 'border border-border bg-surface',
    shadow: 'bg-surface shadow-[0_6px_24px_-12px_rgb(0_0_0/0.25)]',
    flat: 'bg-surface-muted',
  }[style.cards];
}

/** Call-to-action buttons: solid, or pill shaped. */
export function useCta() {
  const { style } = useDesign();
  return (variant: 'primary' | 'light' | 'outline' = 'primary') =>
    cn(
      'inline-flex min-h-11 items-center justify-center gap-2 px-5 text-sm font-semibold transition-opacity hover:opacity-90',
      style.buttons === 'pill' ? 'rounded-full' : 'rounded-md',
      variant === 'primary' && 'bg-primary text-primary-fg',
      variant === 'light' && 'bg-white text-[#111]',
      variant === 'outline' && 'border border-current',
    );
}

/**
 * A section's own button style (ADR-0128 amendment 7): solid, outline, pill or frosted glass,
 * on a picture (`light`) or on the page.
 */
export function useStyledCta() {
  const cta = useCta();
  return (style: 'solid' | 'outline' | 'pill' | 'glass', light: boolean, primary = true) =>
    cn(
      style === 'outline' || !primary
        ? cta('outline')
        : style === 'glass'
          ? cn(
              cta('outline'),
              'border-white/40 bg-white/15 text-white backdrop-blur-md hover:bg-white/25',
            )
          : cta(light ? 'light' : 'primary'),
      style === 'pill' && 'rounded-full',
      style === 'glass' && !light && 'border-border bg-surface/60 text-fg',
    );
}

const RADII = {
  sharp: ['0.125rem', '0.25rem', '0.375rem', '0.5rem'],
  soft: ['0.375rem', '0.625rem', '0.875rem', '1.25rem'],
  round: ['0.625rem', '1rem', '1.5rem', '2rem'],
} as const;

/** CSS variables of the store's colours and shapes, applied to the whole document. */
function themeVariables(design: StoreDesign, primary: string): Record<string, string> {
  const brand = /^#[0-9a-f]{6}$/i.test(primary) ? primary : '#0f766e';
  const fg = readableOn(brand);
  const [sm, md, lg, xl] = RADII[design.style.radius];
  const vars: Record<string, string> = {
    '--color-primary': brand,
    '--color-primary-hover': `color-mix(in oklch, ${brand} 85%, black)`,
    '--color-primary-fg': fg,
    '--color-primary-soft': `color-mix(in oklch, ${brand} 12%, white)`,
    '--radius-sm': sm,
    '--radius-md': md,
    '--radius-lg': lg,
    '--radius-xl': xl,
  };
  if (design.style.background === 'tinted') {
    vars['--color-canvas'] = `color-mix(in oklch, ${brand} 5%, white)`;
    vars['--color-surface-muted'] = `color-mix(in oklch, ${brand} 8%, white)`;
  }
  if (design.style.background === 'dark') {
    Object.assign(vars, {
      '--color-canvas': 'oklch(0.17 0.01 260)',
      '--color-surface': 'oklch(0.22 0.012 260)',
      '--color-surface-muted': 'oklch(0.27 0.014 260)',
      '--color-border': 'oklch(0.33 0.015 260)',
      '--color-border-strong': 'oklch(0.42 0.02 260)',
      '--color-fg': 'oklch(0.96 0.005 260)',
      '--color-fg-muted': 'oklch(0.77 0.01 260)',
      '--color-fg-subtle': 'oklch(0.66 0.01 260)',
      '--color-primary-soft': `color-mix(in oklch, ${brand} 25%, oklch(0.22 0.012 260))`,
    });
  }
  return vars;
}

export function useThemeVariables(primary: string) {
  const design = useDesign();
  const vars = themeVariables(design, primary);
  const key = JSON.stringify(vars);
  useEffect(() => {
    const root = document.documentElement;
    const entries = Object.entries(JSON.parse(key) as Record<string, string>);
    for (const [k, v] of entries) root.style.setProperty(k, v);
    root.style.colorScheme = design.style.background === 'dark' ? 'dark' : 'light';
    // The phone browser's bar takes the store's brand colour.
    const bar = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const before = bar?.content ?? null;
    if (bar && /^#[0-9a-f]{6}$/i.test(primary)) bar.content = primary;
    return () => {
      for (const [k] of entries) root.style.removeProperty(k);
      root.style.colorScheme = '';
      if (bar && before !== null) bar.content = before;
    };
  }, [key, design.style.background, primary]);
}
